const NATIVE_APP_SESSION_KEY = 'papanoelchat_native_app_session'

function hasSessionFlag(): boolean {
  if (typeof window === 'undefined') return false

  try {
    return window.sessionStorage.getItem(NATIVE_APP_SESSION_KEY) === 'true'
  } catch {
    return false
  }
}

function hasNativeProtocol(): boolean {
  if (typeof window === 'undefined') return false

  const protocol = window.location?.protocol
  return protocol === 'capacitor:' || protocol === 'ionic:'
}

export function hasCapacitorBridge(): boolean {
  // OJO: @capacitor/core inyecta `window.Capacitor` TAMBIÉN en web (un shim cuyo
  // isNativePlatform() devuelve false). Su mera presencia NO implica app native;
  // solo confiamos en el bridge cuando reporta plataforma nativa.
  // Se invoca siempre desde cliente (los callers ya guardan `typeof window`).
  const cap = (window as Window & { Capacitor?: { isNativePlatform: () => boolean } }).Capacitor
  return cap?.isNativePlatform() === true
}

export function markNativeAppSession(): void {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(NATIVE_APP_SESSION_KEY, 'true')
  } catch {
    // Ignore storage failures in restricted WebViews.
  }
}

export function hasNativeAppQueryParam(): boolean {
  if (typeof window === 'undefined') return false

  try {
    return new URLSearchParams(window.location.search).get('native_app') === '1'
  } catch {
    return false
  }
}

/**
 * In-app browsers conocidos (Instagram, Facebook, TikTok, WhatsApp…). Son
 * WebViews según el UA (sin `Safari/` en iOS, con `wv` en Android), pero NUNCA
 * tendrán bridge de Capacitor: sin este descarte, la heurística genérica de
 * WebView los hacía esperar el timeout completo del bridge (~5s) con la
 * página en blanco — justo el primer clic del tráfico social.
 */
const IN_APP_BROWSER_UA_MARKERS = [
  'Instagram',
  'FBAN', // Facebook iOS
  'FBAV', // Facebook (iOS y Android)
  'FB_IAB', // Facebook Android in-app browser
  'musical_ly', // TikTok
  'BytedanceWebview', // TikTok
  'WhatsApp',
  'Snapchat',
  'Twitter',
  'Pinterest',
  'LinkedInApp',
  ' Line/', // LINE messenger (con separadores para no pillar "Outline" etc.)
  'GSA/', // Google Search App iOS
] as const

function isKnownInAppBrowser(ua: string): boolean {
  return IN_APP_BROWSER_UA_MARKERS.some((marker) => ua.includes(marker))
}

function hasConcreteCapacitorEvidence(isNativeBootstrapPath: boolean): boolean {
  return (
    (isNativeBootstrapPath && hasNativeAppQueryParam()) ||
    hasNativeProtocol() ||
    hasCapacitorBridge()
  )
}

function isGenericWebViewUserAgent(ua: string): boolean {
  const isIOSWebView =
    /iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari\//.test(ua)
  const isAndroidWebView = /Android/.test(ua) && /wv/.test(ua)

  return isIOSWebView || isAndroidWebView
}

function isStandaloneDisplayMode(): boolean {
  return (
    (window.matchMedia?.('(display-mode: standalone)').matches ?? false) ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function shouldWaitForNativeAppBridge(): boolean {
  if (typeof window === 'undefined') return false

  const isNativeBootstrapPath = window.location.pathname.includes('/mobile')

  // Evidencia concreta de Capacitor: manda sobre cualquier heurística de UA.
  if (hasConcreteCapacitorEvidence(isNativeBootstrapPath)) return true

  const ua = window.navigator?.userAgent ?? ''

  // In-app browser conocido sin evidencia de bridge: renderizar ya.
  if (isKnownInAppBrowser(ua)) return false

  // WebView desconocido: red de seguridad para la app nativa (p.ej. recarga
  // completa con sessionStorage limpio fuera del path de bootstrap).
  if (isGenericWebViewUserAgent(ua)) return true

  return isNativeBootstrapPath && isStandaloneDisplayMode()
}

export function isNativeAppRuntime(): boolean {
  if (typeof window === 'undefined') return false

  if (hasNativeProtocol() || hasCapacitorBridge()) {
    markNativeAppSession()
    return true
  }

  return hasSessionFlag()
}
